"use client";

import { useEffect, useState } from "react";

/**
 * A module for something that is closed on arrival (a drawer, a lightbox), kept out of the page's
 * first-load JS. It is imported once the page has fired `load` and the browser is idle — outside
 * the LCP window, so it is normally there before the first tap — or as soon as `needed` turns true,
 * whichever is first. Returns the module once it has loaded, null until then.
 *
 * Not next/dynamic: that renders through React.lazy, and a lazy component that suspends on open is
 * revealed no sooner than React's 300 ms Suspense throttle, even when its chunk is already cached.
 * A module held in state renders on the same frame as the tap that opens it.
 */
export function useIdleModule<T>(load: () => Promise<T>, needed: boolean): T | null {
  const [mod, setMod] = useState<T | null>(null);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (needed) setRequested(true);
  }, [needed]);

  useEffect(() => {
    // Safari has no requestIdleCallback; it gets a timeout.
    const hasIdle = typeof window.requestIdleCallback === "function";
    let handle: number | undefined;
    const request = () => setRequested(true);
    const schedule = () => {
      handle = hasIdle ? window.requestIdleCallback(request, { timeout: 4000 }) : window.setTimeout(request, 2000);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => {
      window.removeEventListener("load", schedule);
      if (handle === undefined) return;
      if (hasIdle) window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  useEffect(() => {
    if (!requested || mod) return;
    let cancelled = false;
    void load().then((loaded) => {
      if (!cancelled) setMod(() => loaded);
    });
    return () => {
      cancelled = true;
    };
    // `load` is a module-level import thunk; it never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, mod]);

  return mod;
}
